import {
  Component,
  OnInit,
  OnDestroy,
  output,
  OutputEmitterRef,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SocialOffersService } from '../services/social-offers.service';
import { Subject, takeUntil, finalize } from 'rxjs';
import { CouponsButtonComponent } from '../coupons-button/coupons-button.component';
import { CouponsItemResponse } from '../model/coupons-response';
import { ActivatedRoute } from '@angular/router';
import { DomianProviderService } from '../services/domain-provider.service';
import { envs } from '../data/env.data';
import { getBannerFromUrl } from '../services/appconfig.service';

@Component({
  selector: 'app-social-offers-list',
  standalone: true,
  imports: [CommonModule, CouponsButtonComponent],
  providers: [SocialOffersService],
  templateUrl: './social-offers-list.component.html',
  styleUrl: './social-offers-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialOffersListComponent implements OnInit, OnDestroy {
  private readonly socialOffersService = inject(SocialOffersService);
  private readonly route = inject(ActivatedRoute);
  private readonly bannerService = inject(DomianProviderService);

  private readonly destroy$ = new Subject<void>();

  protected socialOffers = signal<CouponsItemResponse[]>([]);
  readonly isLoading = signal<boolean>(false);

  // Output for parent communication
  readonly doLogin: OutputEmitterRef<boolean> = output<boolean>();
  private readonly DEFAULT_OFFER_TYPE = 'SOCIAL';

  readonly hasOffers = computed(() => this.socialOffers().length > 0);
  readonly isEmpty = computed(() => !this.isLoading() && !this.hasOffers());
  readonly banner = computed(() => {
    return envs.useParamsForBanner
      ? getBannerFromUrl()
      : this.bannerService.getBanner();
  });

  // Constants

  ngOnInit(): void {
    this.loadSocialOffers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  doLoginHandler(): void {
    this.doLogin.emit(true);
  }

  private loadSocialOffers(): void {
    const offerType =
      this.route.snapshot.paramMap.get('offer') || this.DEFAULT_OFFER_TYPE;

    this.isLoading.set(true);

    this.socialOffersService
      .getSocialOffers(offerType, this.banner())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (offers) => {
          this.socialOffers.set(offers.coupons || []);
        },
        error: (error) => {
          console.error('Error in social offers subscription:', error);
          this.socialOffers.set([]);
        },
      });
  }
}
